import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';

// @mui
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

// routes
import { PATH_DASHBOARD } from '../../routes/paths';

// hooks
import useSettings from '../../hooks/useSettings';
import useAuth from '../../hooks/useAuth';

// components
import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';

// utils
import axiosInstance from '../../utils/axios';
import modifyScriptName from '../../utils/modifyScriptName';

// ----------------------------------------------------------------------

const EXCHANGE_OPTIONS = ['', 'NSE', 'MCX', 'COMEX', 'CRYPTO', 'FOREX', 'NASDAQ'];

const toDateInputValue = (value) => {
  if (value == null) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const isActiveNow = (enableDate, squareoffDate) => {
  const now = new Date();
  return new Date(enableDate) <= now && new Date(squareoffDate) >= now;
};

// ----------------------------------------------------------------------

export default function ScriptScheduleManage() {
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [exchange, setExchange] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resubscribing, setResubscribing] = useState(false);
  const [docs, setDocs] = useState([]);
  const [edits, setEdits] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [bulkEnable, setBulkEnable] = useState('');
  const [bulkSquareoff, setBulkSquareoff] = useState('');
  const [pagination, setPagination] = useState({ totalDocs: 0 });

  const isSuperAdmin = user?.role?.rolename === 'superAdmin';

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchScripts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (search) params.search = search;
      if (exchange) params.exchange = exchange;

      const res = await axiosInstance.get('/script/schedule-manage', { params });
      const data = res.data?.data || {};
      const list = Array.isArray(data.docs) ? data.docs : [];
      setDocs(list);
      setPagination({ totalDocs: data.pagination?.totalDocs ?? 0 });

      const initialEdits = {};
      list.forEach((row) => {
        const id = row._id;
        initialEdits[id] = {
          enable_date: toDateInputValue(row.enable_date),
          squareoff_date: toDateInputValue(row.squareoff_date),
        };
      });
      setEdits(initialEdits);
      setSelected(new Set());
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to load scripts', { variant: 'error' });
      setDocs([]);
      setEdits({});
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, exchange, enqueueSnackbar]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchScripts();
    }
  }, [fetchScripts, isSuperAdmin]);

  const dirtyUpdates = useMemo(() => {
    const updates = [];
    docs.forEach((row) => {
      const id = row._id;
      const edit = edits[id];
      if (!edit) return;

      const origEnable = toDateInputValue(row.enable_date);
      const origSquareoff = toDateInputValue(row.squareoff_date);

      if (
        edit.enable_date !== origEnable ||
        edit.squareoff_date !== origSquareoff
      ) {
        if (edit.enable_date && edit.squareoff_date) {
          updates.push({
            id,
            enable_date: edit.enable_date,
            squareoff_date: edit.squareoff_date,
          });
        }
      }
    });
    return updates;
  }, [docs, edits]);

  const handleEditChange = (id, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === docs.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(docs.map((d) => d._id)));
    }
  };

  const applyBulkDates = () => {
    if (!bulkEnable && !bulkSquareoff) {
      enqueueSnackbar('Set at least one bulk date', { variant: 'warning' });
      return;
    }
    if (selected.size === 0) {
      enqueueSnackbar('Select scripts to apply bulk dates', { variant: 'warning' });
      return;
    }

    setEdits((prev) => {
      const next = { ...prev };
      selected.forEach((id) => {
        next[id] = {
          enable_date: bulkEnable || next[id]?.enable_date || '',
          squareoff_date: bulkSquareoff || next[id]?.squareoff_date || '',
        };
      });
      return next;
    });
    enqueueSnackbar(`Applied dates to ${selected.size} script(s)`, { variant: 'info' });
  };

  const handleSave = async () => {
    if (dirtyUpdates.length === 0) {
      enqueueSnackbar('No changes to save', { variant: 'info' });
      return;
    }

    setSaving(true);
    try {
      const res = await axiosInstance.post('/script/bulk-schedule', {
        updates: dirtyUpdates,
      });
      const updated = res.data?.data?.updated ?? dirtyUpdates.length;
      enqueueSnackbar(`Updated ${updated} script(s) and resubscribed feeds`, {
        variant: 'success',
      });
      fetchScripts();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to save schedules', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleResubscribe = async () => {
    setResubscribing(true);
    try {
      const res = await axiosInstance.post('/script/resubscribe');
      const count = res.data?.data?.stockapp?.subscribedCount;
      enqueueSnackbar(
        count != null
          ? `Resubscribed ${count} active script(s)`
          : 'Resubscribe request sent',
        { variant: 'success' }
      );
    } catch (err) {
      enqueueSnackbar(err?.message || 'Resubscribe failed', { variant: 'error' });
    } finally {
      setResubscribing(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Page title="Script Schedule">
        <Container>
          <Typography color="error" sx={{ py: 4 }}>
            Access denied. Super Admin only.
          </Typography>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Script Schedule Manage">
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs
          heading="Script Schedule Manage"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.general.dashboard },
            { name: 'Reports', href: PATH_DASHBOARD.report.root },
            { name: 'Schedule Manage' },
          ]}
        />

        <Card sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
            <TextField
              label="Search name / symbol"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              size="small"
              sx={{ minWidth: 220 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Exchange</InputLabel>
              <Select
                label="Exchange"
                value={exchange}
                onChange={(e) => {
                  setExchange(e.target.value);
                  setPage(0);
                }}
              >
                {EXCHANGE_OPTIONS.map((opt) => (
                  <MenuItem key={opt || 'all'} value={opt}>
                    {opt || 'All'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              color="warning"
              onClick={handleResubscribe}
              disabled={resubscribing}
            >
              {resubscribing ? 'Resubscribing…' : 'Resubscribe active'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || dirtyUpdates.length === 0}
            >
              {saving ? 'Saving…' : `Save changes (${dirtyUpdates.length})`}
            </Button>
          </Stack>
        </Card>

        <Card sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Bulk apply to selected ({selected.size})
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              label="Enable date"
              type="date"
              size="small"
              value={bulkEnable}
              onChange={(e) => setBulkEnable(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Square off date"
              type="date"
              size="small"
              value={bulkSquareoff}
              onChange={(e) => setBulkSquareoff(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="outlined" onClick={applyBulkDates}>
              Apply to selected
            </Button>
          </Stack>
        </Card>

        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={docs.length > 0 && selected.size === docs.length}
                      indeterminate={selected.size > 0 && selected.size < docs.length}
                      onChange={toggleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Script</TableCell>
                  <TableCell>Exchange</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Enable date</TableCell>
                  <TableCell>Square off date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && docs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No scripts found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  docs.map((row) => {
                    const id = row._id;
                    const edit = edits[id] || {};
                    const active = isActiveNow(
                      edit.enable_date || row.enable_date,
                      edit.squareoff_date || row.squareoff_date
                    );
                    let displayName = row.tradingsymbol;
                    try {
                      displayName = modifyScriptName(row.tradingsymbol, row.expiry, row);
                    } catch {
                      displayName = row.name || row.tradingsymbol;
                    }

                    return (
                      <TableRow key={id} hover selected={selected.has(id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selected.has(id)}
                            onChange={() => toggleSelect(id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {displayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.tradingsymbol}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.exchange?.name || '—'}</TableCell>
                        <TableCell>{row.expiry || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={active ? 'active' : 'inactive'}
                            color={active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="date"
                            size="small"
                            value={edit.enable_date || ''}
                            onChange={(e) =>
                              handleEditChange(id, 'enable_date', e.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="date"
                            size="small"
                            value={edit.squareoff_date || ''}
                            onChange={(e) =>
                              handleEditChange(id, 'squareoff_date', e.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.totalDocs}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Card>
      </Container>
    </Page>
  );
}
